'use client'

import { useState } from 'react'
import { Sparkles, Heart, TrendingUp, ShieldCheck, PlayCircle } from 'lucide-react'
import { LazyMotion, domAnimation, m, useReducedMotion, type Variants } from 'framer-motion'
import { unsplashPlaceholderFor, type PlaceholderCategory } from '@/lib/copy/placeholder-images'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { EASE, SPRING_SOFT, VIEWPORT, staggerContainer } from '@/lib/motion'

// Swap-style 3-up stat tiles: big number + headline + body + a mini
// phone-style mockup underneath each tile. Use on /home, /features
// or /agentic-shopping. Cards stack on mobile.
//
// Motion (June 2026): the tiles stagger-reveal once on scroll-in and the
// phones settle in on a soft spring a beat behind their tile. The stat
// values are ranges/multiples ('3x', '+34%'), not plain numerics, so
// there is deliberately no count-up — faking one would misread the data.
// Reduced motion resolves straight to the final state.

type Tile = {
  stat: string
  title: string
  body: string
  /** Which mini phone mockup to render under the tile. */
  mock: 'gallery' | 'chat' | 'tryon' | 'reviews'
  /** Image category seed for the mock photo. */
  category?: PlaceholderCategory
  /** Image seed string for the mock photo. */
  seed?: string
  /** Explicit Unsplash photo ID for the backdrop (skips the seed lottery). */
  backdropId?: string
  /** Optional shoppable-video MP4 URL, when set, the phone plays it muted/looped. */
  videoSrc?: string
}

const DEFAULT_TILES: Tile[] = [
  {
    stat: '3x',
    title: 'Increase time on site',
    body: '3x time on site with deeper customer engagement once UGC + conversational surfaces ship.',
    mock: 'gallery',
    category: 'fashion',
    seed: 'tile-gallery',
  },
  {
    stat: '10x',
    title: 'First-party data',
    body: '10x better customer keystroke-level data that captures all intent end-to-end.',
    mock: 'chat',
    category: 'beauty',
    seed: 'tile-chat',
  },
  {
    stat: '-20%',
    title: 'Reduce returns with VTO',
    body: 'Up to 20% reduction in returns with better sizing, fit guidance, and virtual try-on.',
    mock: 'tryon',
    category: 'fashion',
    seed: 'tile-tryon',
  },
]

// Tile settles with a small fade-up; the phone underneath arrives a
// beat later on a soft spring (y 20 -> 0, scale 0.97 -> 1).
const tileItem: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
}

const phoneSettle: Variants = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: { ...SPRING_SOFT, delay: 0.12 } },
}

export function StatTilesWithPhones({
  eyebrow,
  title,
  tiles = DEFAULT_TILES,
}: {
  eyebrow?: string
  title?: string
  tiles?: Tile[]
}) {
  const reduce = useReducedMotion()

  return (
    <section className="container-wide py-16 md:py-24">
      {(eyebrow || title) && (
        <div className="mx-auto mb-12 max-w-3xl text-center">
          {eyebrow && (
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">{eyebrow}</p>
          )}
          {title && (
            <h2 className="mt-3 font-display text-3xl font-medium tracking-tight md:text-4xl">{title}</h2>
          )}
        </div>
      )}

      <LazyMotion features={domAnimation}>
        <m.ul
          className="grid gap-8 md:grid-cols-3 md:gap-10"
          initial={reduce ? false : 'hidden'}
          whileInView={reduce ? undefined : 'show'}
          viewport={VIEWPORT}
          variants={staggerContainer}
        >
          {tiles.map((t) => (
            <m.li
              key={t.title}
              className="flex flex-col items-center text-center"
              variants={reduce ? undefined : tileItem}
            >
              <p className="font-display text-5xl font-semibold tracking-tight md:text-6xl">{t.stat}</p>
              <h3 className="mt-3 font-display text-lg font-semibold md:text-xl">{t.title}</h3>
              <p className="mt-2 max-w-xs text-sm text-muted-foreground md:text-[15px]">{t.body}</p>
              <m.div className="mt-8" variants={reduce ? undefined : phoneSettle}>
                <MiniPhone tile={t} />
              </m.div>
            </m.li>
          ))}
        </m.ul>
      </LazyMotion>
    </section>
  )
}

function MiniPhone({ tile }: { tile: Tile }) {
  // Prefer explicit backdropId, fall back to seed-based hash pick, fall
  // back again to a category-tinted gradient if the image errors. Means
  // the phone always reads as "a real surface" rather than a blank rect.
  const photo = tile.backdropId
    ? `https://images.unsplash.com/${tile.backdropId}?auto=format&fit=crop&w=400&h=800&q=80`
    : unsplashPlaceholderFor(tile.seed || tile.title, {
        category: tile.category || 'fashion',
        width: 400,
        height: 800,
      })
  const [imgErrored, setImgErrored] = useState(false)
  const gradientByCategory: Record<string, string> = {
    fashion:   'from-rose-300/60 via-rose-200/30 to-amber-100/30',
    beauty:    'from-fuchsia-300/60 via-rose-200/40 to-amber-100/30',
    jewellery: 'from-amber-300/60 via-amber-200/40 to-rose-100/30',
    home:      'from-emerald-300/60 via-emerald-200/40 to-sky-100/30',
    sport:     'from-indigo-300/60 via-sky-200/40 to-emerald-100/30',
  }
  const fallbackGradient = gradientByCategory[tile.category || 'fashion'] ?? gradientByCategory.fashion

  return (
    <div className="relative mx-auto h-[300px] w-[150px] overflow-hidden rounded-[24px] border-4 border-foreground bg-foreground shadow-xl">
      {/* Notch */}
      <div className="absolute left-1/2 top-1.5 z-10 h-2.5 w-12 -translate-x-1/2 rounded-full bg-foreground/80 ring-1 ring-white/10" />

      <div className="relative h-full w-full overflow-hidden rounded-[18px] bg-background">
        {/* Gradient base, always present, becomes the visible backdrop if the image / video fails */}
        <div className={`absolute inset-0 bg-gradient-to-br ${fallbackGradient}`} />

        {/* Video backdrop wins when provided */}
        {tile.videoSrc && !imgErrored ? (
          <video
            src={tile.videoSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onError={() => setImgErrored(true)}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : !imgErrored ? (
          /* Photo backdrop. On error the always-present gradient base
             shows through, so the fallback tile stays invisible. */
          <ImgWithFallback
            src={photo}
            alt=""
            fill
            sizes="150px"
            fallbackClassName="opacity-0"
            onError={() => setImgErrored(true)}
            className="object-cover"
          />
        ) : null}

        {/* Subtle play-glyph for videos so the eye reads "live demo" */}
        {tile.videoSrc && !imgErrored && (
          <span className="absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-foreground/80 px-1.5 py-0.5 text-[8px] font-bold text-background backdrop-blur">
            <PlayCircle className="h-2.5 w-2.5" /> Live
          </span>
        )}

        {/* Overlay content per mock kind */}
        {tile.mock === 'gallery' && (
          <div className="absolute inset-x-2 bottom-2 grid grid-cols-2 gap-1.5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="relative aspect-square overflow-hidden rounded-md bg-background/70 ring-1 ring-white/40">
                <ImgWithFallback
                  src={unsplashPlaceholderFor(`mp-${tile.seed}-${i}`, { category: tile.category || 'fashion', width: 200, height: 200 })}
                  alt=""
                  fill
                  sizes="70px"
                  className="object-cover"
                />
              </div>
            ))}
          </div>
        )}

        {tile.mock === 'chat' && (
          <div className="absolute inset-x-2 bottom-3 space-y-1.5 text-[9px]">
            <div className="ml-auto max-w-[80%] rounded-[10px] rounded-tr-sm bg-sky-500 px-2 py-1 text-white">What works for combo skin?</div>
            <div className="flex items-start gap-1.5">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/90 text-white"><Sparkles className="h-2 w-2" /></span>
              <div className="max-w-[80%] rounded-[10px] rounded-tl-sm bg-card/95 px-2 py-1">Try our gentle gel cleanser + lightweight moisturiser.</div>
            </div>
            <div className="ml-5 rounded-[10px] bg-card/95 px-1.5 py-1">
              <div className="flex items-center gap-1">
                <div className="h-4 w-4 rounded-sm bg-secondary" />
                <p className="text-[8px] font-semibold">Hydro-balance set</p>
              </div>
            </div>
          </div>
        )}

        {tile.mock === 'tryon' && (
          <>
            <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-foreground/85 px-2 py-0.5 text-[8px] font-bold text-background backdrop-blur">
              <Sparkles className="h-2 w-2" /> Try on
            </span>
            <div className="absolute inset-x-2 bottom-2 rounded-[10px] bg-card/95 p-1.5 text-[9px]">
              <div className="flex items-center justify-between">
                <span className="font-semibold">£21</span>
                <button className="rounded-full bg-emerald-500 px-2 py-0.5 text-[8px] font-bold text-foreground">Add</button>
              </div>
              <p className="text-[7px] text-muted-foreground">Black Eye Liner</p>
            </div>
          </>
        )}

        {tile.mock === 'reviews' && (
          <div className="absolute inset-x-2 bottom-2 rounded-[10px] bg-card/95 p-1.5 text-[9px]">
            <div className="flex items-center gap-1">
              <Heart className="h-2.5 w-2.5 fill-rose-500 text-rose-500" />
              <p className="font-semibold">4.9 · 1,204 reviews</p>
            </div>
            <p className="mt-0.5 line-clamp-2 text-[8px] text-muted-foreground">&ldquo;Best linen I&apos;ve worn, true to size and doesn&apos;t wrinkle.&rdquo;</p>
          </div>
        )}
      </div>
    </div>
  )
}
